import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ShareCreationWizard = ({ isOpen, onClose, onCreateShare }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [shareData, setShareData] = useState({
    type: 'internal',
    recipients: [],
    items: [],
    accessLevel: 'viewer',
    expiresAt: '',
    usageLimit: '',
    requireApproval: false,
    allowOffline: false,
    domainRestrictions: [],
    passphrase: ''
  });

  const steps = [
    { id: 1, title: 'Share Type', icon: 'Share2' },
    { id: 2, title: 'Recipients', icon: 'Users' },
    { id: 3, title: 'Items', icon: 'Key' },
    { id: 4, title: 'Permissions', icon: 'Shield' },
    { id: 5, title: 'Security', icon: 'Lock' }
  ];

  const accessLevels = [
    { value: 'viewer', label: 'Viewer', description: 'Can view items only' },
    { value: 'contributor', label: 'Contributor', description: 'Can view and edit items' },
    { value: 'manager', label: 'Manager', description: 'Full access including sharing' }
  ];

  const mockUsers = [
    { value: 'user1', label: 'John Smith', description: 'Engineering Department' },
    { value: 'user2', label: 'Sarah Johnson', description: 'Marketing Department' },
    { value: 'user3', label: 'Mike Davis', description: 'Sales Department' }
  ];

  const mockItems = [
    { value: 'item1', label: 'AWS Production Keys', description: 'API Keys' },
    { value: 'item2', label: 'Database Credentials', description: 'Login' },
    { value: 'item3', label: 'GitHub Token', description: 'API Keys' }
  ];

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCreateShare = () => {
    onCreateShare(shareData);
    onClose();
    setCurrentStep(1);
    setShareData({
      type: 'internal',
      recipients: [],
      items: [],
      accessLevel: 'viewer',
      expiresAt: '',
      usageLimit: '',
      requireApproval: false,
      allowOffline: false,
      domainRestrictions: [],
      passphrase: ''
    });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Choose Share Type</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setShareData({ ...shareData, type: 'internal' })}
                  className={`p-6 border-2 rounded-lg text-left transition-smooth ${
                    shareData?.type === 'internal' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="Users" size={20} className="text-primary" />
                    </div>
                    <h4 className="font-medium text-foreground">Internal Sharing</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Share with team members and departments within your organization
                  </p>
                </button>

                <button
                  onClick={() => setShareData({ ...shareData, type: 'external' })}
                  className={`p-6 border-2 rounded-lg text-left transition-smooth ${
                    shareData?.type === 'external' ?'border-accent bg-accent/5' :'border-border hover:border-accent/50'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Icon name="ExternalLink" size={20} className="text-accent" />
                    </div>
                    <h4 className="font-medium text-foreground">External Sharing</h4>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Share with external partners, contractors, or guests
                  </p>
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Select Recipients</h3>
              {shareData?.type === 'internal' ? (
                <Select
                  label="Team Members"
                  description="Select users from your organization"
                  options={mockUsers}
                  value={shareData?.recipients}
                  onChange={(value) => setShareData({ ...shareData, recipients: value })}
                  multiple
                  searchable
                />
              ) : (
                <div className="space-y-4">
                  <Input
                    label="Guest Email"
                    type="email"
                    placeholder="Enter guest email address"
                    description="External user will receive an invitation"
                  />
                  <Input
                    label="Guest Name"
                    type="text"
                    placeholder="Enter guest name"
                  />
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Select Items to Share</h3>
              <Select
                label="Credentials & Secrets"
                description="Choose items to share with recipients"
                options={mockItems}
                value={shareData?.items}
                onChange={(value) => setShareData({ ...shareData, items: value })}
                multiple
                searchable
              />
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Set Permissions</h3>
              <Select
                label="Access Level"
                description="Define what recipients can do with shared items"
                options={accessLevels}
                value={shareData?.accessLevel}
                onChange={(value) => setShareData({ ...shareData, accessLevel: value })}
              />
              
              <div className="mt-6 space-y-4">
                <Checkbox
                  label="Require approval for sensitive actions"
                  description="Recipients must request approval for certain operations"
                  checked={shareData?.requireApproval}
                  onChange={(e) => setShareData({ ...shareData, requireApproval: e?.target?.checked })}
                />
                <Checkbox
                  label="Allow offline access"
                  description="Recipients can cache items for offline use"
                  checked={shareData?.allowOffline}
                  onChange={(e) => setShareData({ ...shareData, allowOffline: e?.target?.checked })}
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-foreground mb-4">Security Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Expiration Date"
                  type="datetime-local"
                  description="When should access expire?"
                  value={shareData?.expiresAt}
                  onChange={(e) => setShareData({ ...shareData, expiresAt: e?.target?.value })}
                />
                <Input
                  label="Usage Limit"
                  type="number"
                  placeholder="e.g., 10"
                  description="Maximum number of accesses"
                  value={shareData?.usageLimit}
                  onChange={(e) => setShareData({ ...shareData, usageLimit: e?.target?.value })}
                />
              </div>
              
              {shareData?.type === 'external' && (
                <div className="mt-6 space-y-4">
                  <Input
                    label="Access Passphrase"
                    type="password"
                    placeholder="Enter secure passphrase"
                    description="Additional security for external access"
                    value={shareData?.passphrase}
                    onChange={(e) => setShareData({ ...shareData, passphrase: e?.target?.value })}
                  />
                  <Input
                    label="Domain Restrictions"
                    type="text"
                    placeholder="company.com, partner.org"
                    description="Comma-separated list of allowed domains"
                  />
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-300 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Create New Share</h2>
            <p className="text-sm text-muted-foreground">Step {currentStep} of {steps?.length}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => (
              <div key={step?.id} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-smooth ${
                  currentStep >= step?.id
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border bg-background text-muted-foreground'
                }`}>
                  {currentStep > step?.id ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    <Icon name={step?.icon} size={16} />
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {step?.title}
                </span>
                {index < steps?.length - 1 && (
                  <div className={`w-12 h-0.5 mx-4 ${
                    currentStep > step?.id ? 'bg-primary' : 'bg-border'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
          >
            <Icon name="ChevronLeft" size={16} className="mr-2" />
            Previous
          </Button>
          
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {currentStep === steps?.length ? (
              <Button onClick={handleCreateShare}>
                <Icon name="Share2" size={16} className="mr-2" />
                Create Share
              </Button>
            ) : (
              <Button onClick={handleNext}>
                Next
                <Icon name="ChevronRight" size={16} className="ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareCreationWizard;